(() => {
  'use strict';
  const root = () => window.Shopify?.routes?.root || '/';
  const reduce = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });

  // Adds business days, skipping Saturdays and Sundays.
  function addBusinessDays(date, days) {
    const d = new Date(date);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
    let left = days;
    while (left > 0) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) left--;
    }
    return d;
  }
  const range = (a, b) => (fmt.format(a) === fmt.format(b) ? fmt.format(a) : `${fmt.format(a)} – ${fmt.format(b)}`);

  function initDelivery(el) {
    const n = (k) => Math.max(0, Number(el.dataset[k]) || 0);
    const pMin = Math.min(n('procMin'), n('procMax')), pMax = Math.max(n('procMin'), n('procMax'));
    const sMin = Math.min(n('shipMin'), n('shipMax')), sMax = Math.max(n('shipMin'), n('shipMax'));
    const today = new Date();
    const shipA = addBusinessDays(today, pMin), shipB = addBusinessDays(today, pMax);
    el.querySelector('[data-ml-date=ordered]').textContent = fmt.format(today);
    el.querySelector('[data-ml-date=shipped]').textContent = range(shipA, shipB);
    el.querySelector('[data-ml-date=delivered]').textContent = range(addBusinessDays(shipA, sMin), addBusinessDays(shipB, sMax));
  }

  // Last order date that still arrives by the "deliver by" day, using the maximum processing + shipping days.
  function initCutoff(el) {
    const [m, d] = (el.dataset.christmas || '12-24').split('-').map(Number);
    const pMax = Number(el.dataset.procMax) || 0, sMax = Number(el.dataset.shipMax) || 0;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const target = new Date(today.getFullYear(), m - 1, d);
    let cutoff = null;
    for (let c = new Date(target); c >= today; c.setDate(c.getDate() - 1)) {
      if (c.getDay() === 0 || c.getDay() === 6) continue;
      if (addBusinessDays(addBusinessDays(c, pMax), sMax) <= target) { cutoff = new Date(c); break; }
    }
    const title = el.querySelector('[data-ml-cutoff-title]'), text = el.querySelector('[data-ml-cutoff-text]');
    if (cutoff) {
      text.textContent = text.dataset.before.replace('[date]', fmt.format(cutoff));
    } else {
      if (title.dataset.after) title.textContent = title.dataset.after;
      text.textContent = text.dataset.after || '';
    }
  }

  function initGallery(el) {
    const track = el.querySelector('[data-ml-track]');
    const thumbs = [...el.querySelectorAll('[data-ml-thumb]')];
    if (!track) return;
    const go = (i) => track.scrollTo({ left: i * track.clientWidth, behavior: reduce() ? 'auto' : 'smooth' });
    thumbs.forEach((t) => t.addEventListener('click', () => go(Number(t.dataset.mlThumb))));
    let raf;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = Math.round(track.scrollLeft / track.clientWidth);
        thumbs.forEach((t, j) => t.setAttribute('aria-current', String(i === j)));
      });
    }, { passive: true });
    track.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const i = Math.round(track.scrollLeft / track.clientWidth) + (e.key === 'ArrowRight' ? 1 : -1);
      go(Math.max(0, Math.min(track.children.length - 1, i)));
    });
  }

  function initProduct(section) {
    const form = section.querySelector('[data-ml-form]');
    if (!form) return;
    const qty = form.querySelector('[data-ml-qty-input]');
    const mirror = section.querySelector('[data-ml-qty-mirror]');
    const status = form.querySelector('[data-ml-status]');
    const addButtons = [...section.querySelectorAll('[data-ml-add]')];
    const variants = JSON.parse(section.querySelector('[data-ml-variants]')?.textContent || '[]');
    const sync = () => { if (mirror) mirror.textContent = qty.value; };

    section.querySelectorAll('[data-ml-qty]').forEach((b) => b.addEventListener('click', () => {
      const step = Number(qty.step) || 1, min = Number(qty.min) || 1, max = qty.max ? Number(qty.max) : Infinity;
      qty.value = String(Math.max(min, Math.min(max, (Number(qty.value) || min) + Number(b.dataset.mlQty) * step)));
      sync();
    }));
    qty.addEventListener('input', sync);

    const select = form.querySelector('[data-ml-variant]');
    select?.addEventListener('change', () => {
      const v = variants.find((x) => String(x.id) === select.value);
      if (!v) return;
      section.querySelector('[data-ml-price]').innerHTML = `<span class="ml-price__now">${v.price}</span>${v.compare ? `<s class="ml-price__was"><span class="sr-only">Regular price</span>${v.compare}</s>` : ''}`;
      const sticky = section.querySelector('.ml-sticky__price');
      if (sticky) sticky.textContent = v.price;
      addButtons.forEach((b) => { b.disabled = !v.available; b.textContent = v.available ? 'Add to cart' : 'Sold out'; });
      const url = new URL(location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState({}, '', url);
    });

    form.addEventListener('submit', async (e) => {
      if (e.submitter && !e.submitter.matches('[data-ml-add]')) return;
      e.preventDefault();
      if (!form.reportValidity()) return;
      addButtons.forEach((b) => { b.disabled = true; b.textContent = 'Adding…'; });
      status.textContent = '';
      try {
        const res = await fetch(`${root()}cart/add.js`, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || 'Unable to add this item. Please try again.');
        location.assign(`${root()}cart`);
      } catch (err) {
        status.textContent = err.message;
        status.focus();
        addButtons.forEach((b) => { b.disabled = false; b.textContent = 'Add to cart'; });
      }
    });

    // Sticky bar: shown once the main Add to cart button has scrolled above the viewport.
    const sticky = section.querySelector('[data-ml-sticky]');
    const mainAdd = form.querySelector('[data-ml-add]');
    if (sticky && mainAdd && 'IntersectionObserver' in window) {
      const focusables = sticky.querySelectorAll('button');
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        focusables.forEach((b) => (b.tabIndex = show ? 0 : -1));
      }).observe(mainAdd);
    }
  }

  function initCarousel(el) {
    const track = el.querySelector('[data-ml-carousel-track]');
    const prev = el.querySelector('[data-ml-prev]'), next = el.querySelector('[data-ml-next]');
    if (!track) return;
    // Lazy images inside a horizontal scroller can stay blank on mobile Safari/Chrome;
    // load them all once the carousel approaches the viewport.
    const lazy = track.querySelectorAll('img[loading=lazy]');
    if (lazy.length && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        lazy.forEach((img) => (img.loading = 'eager'));
        io.disconnect();
      }, { rootMargin: '400px 0px' });
      io.observe(track);
    }
    const update = () => {
      prev.disabled = track.scrollLeft < 4;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    };
    const step = () => (track.children[0]?.getBoundingClientRect().width || track.clientWidth) + (parseFloat(getComputedStyle(track).columnGap) || 0);
    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduce() ? 'auto' : 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduce() ? 'auto' : 'smooth' }));
    track.addEventListener('scroll', update, { passive: true });
    addEventListener('resize', update);
    update();
  }

  function initComments(el) {
    const more = el.querySelector('[data-ml-fb-more]');
    more?.addEventListener('click', () => {
      const extra = el.querySelectorAll('[data-ml-fb-extra]');
      const open = more.getAttribute('aria-expanded') === 'true';
      extra.forEach((li) => (li.hidden = open));
      more.setAttribute('aria-expanded', String(!open));
      more.textContent = open ? 'View more comments' : 'Show fewer comments';
      if (!open) extra[0]?.querySelector('.ml-fb__name')?.setAttribute('tabindex', '-1'), extra[0]?.querySelector('.ml-fb__name')?.focus();
    });
  }

  // Spot the Legs: the legs hide in a random slot each round; taps near the shoes win, decoys answer with data-miss.
  function initSpot(el) {
    const svg = el.querySelector('[data-ml-spot-svg]'), legs = el.querySelector('[data-ml-spot-legs]'), fx = el.querySelector('[data-ml-spot-fx]');
    const frame = el.querySelector('[data-ml-spot-frame]'), startBox = el.querySelector('[data-ml-spot-start]');
    const timerEl = el.querySelector('[data-ml-spot-timer]'), bestEl = el.querySelector('[data-ml-spot-best]'), msg = el.querySelector('[data-ml-spot-msg]');
    const result = el.querySelector('[data-ml-spot-result]'), slots = [...svg.querySelectorAll('[data-slot]')];
    const copy = JSON.parse(el.querySelector('[data-ml-spot-copy]')?.textContent || '{}');
    const hintDelay = (Number(el.dataset.hintDelay) || 8) * 1000;
    const misses = ['Not there… keep looking!', 'Nope! Look for red shoes.', 'Getting warmer… maybe.', 'So close. Or not.'];
    const NS = 'http://www.w3.org/2000/svg';
    let playing = false, t0 = 0, raf = 0, hints = [], current = null, lastSlot = null;
    legs.style.display = 'none';

    const best = () => { try { return Number(localStorage.getItem('ml-spot-best')) || 0; } catch (e) { return 0; } };
    const showBest = () => { const b = best(); bestEl.hidden = !b; if (b) bestEl.textContent = `Best: ${b.toFixed(1)}s`; };
    const toSvg = (x, y) => new DOMPoint(x, y).matrixTransform(svg.getScreenCTM().inverse());
    const shoes = () => new DOMPoint(15, 50).matrixTransform(svg.getScreenCTM().inverse().multiply(legs.getScreenCTM()));
    const say = (html) => { msg.innerHTML = html; };
    const add = (tag, attrs, cls) => { const n = document.createElementNS(NS, tag); Object.entries(attrs).forEach(([k, v]) => n.setAttribute(k, v)); if (cls) n.setAttribute('class', cls); fx.appendChild(n); return n; };

    function tick() { timerEl.textContent = `${((performance.now() - t0) / 1000).toFixed(1)}s`; raf = requestAnimationFrame(tick); }

    function start() {
      fx.replaceChildren();
      result.hidden = true;
      const pool = slots.filter((s) => s !== lastSlot);
      current = pool[Math.floor(Math.random() * pool.length)];
      lastSlot = current;
      legs.setAttribute('transform', current.dataset.transform);
      legs.classList.remove('is-found');
      current.appendChild(legs);
      legs.style.display = '';
      frame.classList.add('is-playing');
      startBox.hidden = true;
      say('Tap where you think the legs are hiding.');
      playing = true;
      t0 = performance.now();
      cancelAnimationFrame(raf); tick();
      hints.forEach(clearTimeout);
      hints = [
        setTimeout(() => playing && say(`Hint: try near ${current.dataset.name}.`), hintDelay),
        setTimeout(() => {
          if (!playing) return;
          const p = shoes();
          add('circle', { cx: p.x, cy: p.y, r: 16 }, 'ml-spot__pulse');
          say(`Look for the glowing spot near ${current.dataset.name}. <button type="button" class="ml-spot__reveal" data-ml-spot-reveal>Show me</button>`);
        }, hintDelay * 2),
      ];
    }

    function win(revealed) {
      playing = false;
      cancelAnimationFrame(raf); hints.forEach(clearTimeout);
      const secs = (performance.now() - t0) / 1000;
      const p = shoes();
      fx.replaceChildren();
      add('circle', { cx: p.x, cy: p.y, r: 20 }, 'ml-spot__ring');
      for (let i = 0; i < 10; i++) {
        const ang = (i / 10) * Math.PI * 2;
        const s = add('path', { d: 'M0-5 1.5-1.5 5 0 1.5 1.5 0 5-1.5 1.5-5 0-1.5-1.5z', style: `--sx:${Math.cos(ang) * 30}px;--sy:${Math.sin(ang) * 30}px`, fill: ['var(--ml-gold)', 'var(--ml-red)', 'var(--ml-frost)'][i % 3], transform: `translate(${p.x} ${p.y})` }, 'ml-spot__spark');
        s.style.animationDelay = `${i * 15}ms`;
      }
      legs.classList.add('is-found');
      el.querySelector('[data-ml-spot-result-time]').textContent = revealed ? 'There they were!' : (copy.found || 'Found them in [time]!').replace('[time]', `${secs.toFixed(1)}s`);
      el.querySelector('[data-ml-spot-result-rank]').textContent = revealed ? 'Now you know where to look. Want another round?' : secs < 3 ? copy.fast : secs < 8 ? copy.mid : copy.slow;
      if (!revealed && (!best() || secs < best())) { try { localStorage.setItem('ml-spot-best', secs.toFixed(2)); } catch (e) {} }
      showBest();
      say('&nbsp;');
      setTimeout(() => { result.hidden = false; result.focus({ preventScroll: true }); }, 650);
    }

    svg.addEventListener('click', (e) => {
      if (!playing) return;
      const pt = toSvg(e.clientX, e.clientY), s = shoes();
      const scale = Math.abs(svg.getScreenCTM().inverse().a) || 1;
      const reach = Math.max(18, 22 * scale);
      if (e.target.closest('[data-ml-spot-legs]') || Math.hypot(pt.x - s.x, pt.y - s.y) < reach) { win(false); return; }
      const miss = e.target.closest('[data-miss]');
      say(miss ? miss.dataset.miss : misses[Math.floor(Math.random() * misses.length)]);
      const x = add('text', { x: pt.x, y: pt.y, 'text-anchor': 'middle', 'dominant-baseline': 'central' }, 'ml-spot__x');
      x.textContent = '✕';
      x.addEventListener('animationend', () => x.remove());
    });

    el.querySelector('[data-ml-spot-go]').addEventListener('click', start);
    el.querySelector('[data-ml-spot-again]').addEventListener('click', start);
    msg.addEventListener('click', (e) => { if (e.target.closest('[data-ml-spot-reveal]')) win(true); });
    el.querySelector('[data-ml-spot-cta]').addEventListener('click', () => {
      const form = document.querySelector('[data-ml-form]'), btn = form?.querySelector('[data-ml-add]');
      if (form && btn && !btn.disabled) form.requestSubmit(btn);
      else document.querySelector('.ml-buy')?.scrollIntoView({ behavior: reduce() ? 'auto' : 'smooth', block: 'start' });
    });
    const apply = el.querySelector('[data-ml-spot-apply]');
    apply?.addEventListener('click', async () => {
      try { await fetch(`${root()}discount/${encodeURIComponent(apply.dataset.mlSpotApply)}`, { credentials: 'same-origin' }); apply.textContent = 'Applied ✓'; apply.disabled = true; } catch (e) { apply.textContent = 'Use it at checkout'; }
    });
    showBest();
  }

  function init(scope = document) {
    const run = (sel, fn) => scope.querySelectorAll(sel).forEach((el) => { if (el.dataset.mlReady) return; el.dataset.mlReady = '1'; fn(el); });
    run('[data-ml-delivery]', initDelivery);
    run('[data-ml-cutoff]', initCutoff);
    run('[data-ml-gallery]', initGallery);
    run('[data-ml-product]', initProduct);
    run('[data-ml-carousel]', initCarousel);
    run('[data-ml-fb]', initComments);
    run('[data-ml-spot]', initSpot);
  }
  init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
